import React, { useCallback, useMemo, useState } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../common/types';
import { CoverStage } from './canvas/CoverStage';
import { useEditorKeys } from './canvas/useEditorKeys';
import { CoverEditorContext, type CoverEditorContextValue, type CoverTool } from './CoverEditorContext';
import { Inspector } from './inspector/Inspector';
import { SidePanel } from './panels/SidePanel';
import { ToolRail } from './panels/ToolRail';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from './CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CoverEditorShellProps {
  /** The editor of the mode: everything but the open tool, which the shell holds. */
  base: Omit<CoverEditorContextValue, 'tool' | 'setTool'>;
  /** The top bar of the mode. It gets the zoom of the stage. */
  renderTopBar: (zoom: number, onZoom: (zoom: number) => void) => React.ReactNode;
  /** The notes over the stage. */
  notices?: React.ReactNode;
  /** The strip under the body (the versions of a song). */
  bottom?: React.ReactNode;
  /** True when the download carries the watermark: the stage can preview it. */
  hasWatermark: boolean;
  /** The dialogs of the mode. They render inside the editor context. */
  children?: React.ReactNode;
}

/** True on a phone-wide screen: the tool panel starts closed, so the cover shows. */
function isNarrowScreen(): boolean {
  return typeof window !== 'undefined' && window.matchMedia?.('(max-width: 900px)').matches === true;
}

/**
 * The frame of the cover editor that both modes share (design cover-editor b1, g1): the tool rail
 * and its panel, the stage and the inspector, with the open tool and the zoom. The song editor
 * (`CoverEditor`) and the public tool (`PublicCoverEditor`) give it their document, their top bar,
 * their notes and their dialogs. There is one editor: a mode never copies a panel or the stage.
 */
const CoverEditorShellComponent: React.FC<CoverEditorShellProps> = (props) => {
  const { base, renderTopBar, notices, bottom, hasWatermark, children } = props;
  const [tool, setTool] = useState<CoverTool>('picture');
  const [isPanelOpen, setIsPanelOpen] = useState(() => !isNarrowScreen());
  const [zoom, setZoom] = useState(1);
  useEditorKeys(base.state, base.dispatch, !base.readOnly);

  const pickTool = useCallback(
    (next: CoverTool) => {
      setIsPanelOpen(next !== tool || !isPanelOpen);
      setTool(next);
    },
    [isPanelOpen, tool]
  );
  const openTool = useCallback((next: CoverTool) => {
    setTool(next);
    setIsPanelOpen(true);
  }, []);
  const context = useMemo<CoverEditorContextValue>(
    () => ({ ...base, tool, setTool: openTool }),
    [base, openTool, tool]
  );

  return (
    <CoverEditorContext.Provider value={context}>
      <div className={clsx(styles.editor, bottom === undefined && styles.editorNoStrip)}>
        {renderTopBar(zoom, setZoom)}
        <div
          className={clsx(
            styles.body,
            !isPanelOpen && styles.bodyNoPane,
            base.state.selection !== null && styles.bodyHasSelection
          )}
        >
          <ToolRail isPanelOpen={isPanelOpen} onPick={pickTool} />
          {isPanelOpen && <SidePanel />}
          <div className={styles.stageColumn}>
            {notices}
            <CoverStage zoom={zoom} hasWatermark={hasWatermark} />
          </div>
          <Inspector />
        </div>
        {bottom}
      </div>
      {children}
    </CoverEditorContext.Provider>
  );
};

export const CoverEditorShell = TypedMemo(CoverEditorShellComponent);
